import React from 'react'
import {
  IItem,
  IItemBinLocation,
  IDefaultBinLocation,
  IIItemDao$ICompanion$IDefaultBinLocationUpdaterInstruction,
  IStockTransferData,
  IAutocompleteEntry,
  IItemMovementHistoryEntry,
  IItemWithoutBarcodeAtLocation,
  IItemLabelPrintJob,
  IIItemDao$ICompanion$IBusinessPartnerSpecificItemPrice,
  IBinLocation,
  ManagedBy,
  ISerialNumber,
  IItemOpenDocuments,
  IPreReceiptPrintLabelJob,
  IStockTransferService$IStockTransferKardexInfo,
  IWholeToPartDto
} from 'interfaces';
import { EBarcodeFields, IIItemDao$ICompanion$IPriceList, IIItemDao$ICompanion$IPriceListUpdateInstructions, IIItemDao$ICompanion$ISetDefaultBinLocation, IIItemDao$ICompanion$IStockTransferRequest, IIItemDao$ICompanion$ITranslationUpdateInstructions } from 'interfaces/erp-interfaces';
import Translation from 'utils/Language/Translation';
import { addNotification } from 'utils/NotificationManager';
import { Registry } from '.';
import { check, distinct, distinctBy, insertBetween, mapAsync, saveBase64UrlEncode } from 'component_utils/utils';
import { confirm, prompt } from 'utils/Prompts';
import Item from 'components/objects/item';
import Location from 'components/objects/location';

export type AppName =
  | 'BURST'
  | 'GOODS SORTING'
  | 'STOCK TRANSFER'
  | 'REPLENISHMENT'
  | 'KARDEX REPLENISHMENT'
  | 'LOCATION TRANSFER'
  | 'STOCK STORAGE'
  | 'KARDEX STORAGE'
  | 'STOCK PICKING'
  | 'MATERIAL CONFIRMATION'
  | 'PICK AND COLLECT'
  | 'MULTI STOCK TRANSFER';

export default ({ get, post }: Registry) => {
  return {
    item: {
      findItemBy: (args: any): Promise<IItem[]> => post('/api/v1/item/find_by', args),
      updateItem: (code: string, args: any) => post(`/api/v1/item/${saveBase64UrlEncode(code)}/update`, args),

      findItem: (code: string): Promise<IItem> => get(`/api/v1/item/find/${saveBase64UrlEncode(code)}`),
      findMultipleItem: (codes: string[]): Promise<IItem[]> => post(`/api/v1/item/find`, codes),
      getItem: (code: string): Promise<IItem> => get(`/api/v1/item/${saveBase64UrlEncode(code)}`),
      getMultipleItems: (codes: string[]): Promise<IItem[]> => post(`/api/v1/item/get`, codes),
      getItemLocations: (code: string): Promise<Array<IItemBinLocation>> => get(`/api/v1/item/${saveBase64UrlEncode(code)}/bin_locations`),
      getItemLocationForMultiple: (codes: string[]) => post<IItemBinLocation[]>(`/api/v1/item/bin_locations_for_multiple`, codes),
      getItemsWithoutLocations: (): Promise<IItemWithoutBarcodeAtLocation[]> => get(`/api/v1/item/without_barcodes`),
      getDefaultLocations: (code: string): Promise<IDefaultBinLocation[]> =>
        get(`/api/v1/item/${saveBase64UrlEncode(code)}/default_bin_locations`),
      setDefaultLocation: (
        code: string,
        whsCode: string,
        data: IIItemDao$ICompanion$ISetDefaultBinLocation,
      ): Promise<void> => post(`/api/v1/item/${saveBase64UrlEncode(code)}/default_bin_locations/${saveBase64UrlEncode(whsCode)}`, data),
      setDefaultLocationWithLocationQty: (
        code: string,
        data: IIItemDao$ICompanion$IDefaultBinLocationUpdaterInstruction,
      ): Promise<void> => post(`/api/v1/item/${saveBase64UrlEncode(code)}/default_bin_locations`, data),
      setBarcode: (code: string, field: EBarcodeFields, barcode: string, barcodeInvQty: number): Promise<void> => post(`/api/v1/item/${saveBase64UrlEncode(code)}/${field}/barcode`, { barcode, barcodeInvQty }),

      setManagedBy: (code: string, managedBy: ManagedBy, newBatchName: string, newSerialPrefix: string) => post(
        `/api/v1/item/${saveBase64UrlEncode(code)}/managedBy/${managedBy}`, { newBatchName, newSerialPrefix }
      ),

      findAutocomplete: (code: string): Promise<IAutocompleteEntry[]> => get(`/api/v1/item/autocomplete/${saveBase64UrlEncode(code)}`),

      getPriceLists: (): Promise<IIItemDao$ICompanion$IPriceList[]> => get(`/api/v1/item/pricelists`),
      getBusinessPartnerPrice: (
        businessPartnerCode: string, 
        itemCode: string,
        quantity?: number,
      ): Promise<IIItemDao$ICompanion$IBusinessPartnerSpecificItemPrice> => get(`/api/v1/item/${saveBase64UrlEncode(itemCode)}/itemPrice/${saveBase64UrlEncode(businessPartnerCode)}/${quantity}`),
      preValidatePriceLists: (data: IIItemDao$ICompanion$IPriceListUpdateInstructions[]): Promise<{[k:string]: string}> => post(`/api/v1/item/pricelists/validate`, data),
      setPriceLists: (data: IIItemDao$ICompanion$IPriceListUpdateInstructions[]): Promise<{
        processed: string[],
        errors: {
          id: string,
          message: string,
          args: any
        }[]
      }> => post(`/api/v1/item/pricelists/update`, data),
      setTranslations: (data: IIItemDao$ICompanion$ITranslationUpdateInstructions[]): Promise<string[]> => post(`/api/v1/item/translations/update`, data),

      getOpenDocuments: (code: string): Promise<IItemOpenDocuments[]> => get(`/api/v1/item/${saveBase64UrlEncode(code)}/open_documents`),
      getAllOpenDocuments: (): Promise<IItemOpenDocuments[]> => get(`/api/v1/item/open_documents`),
      getHistory: (code: string): Promise<IItemMovementHistoryEntry[]> => get(`/api/v1/item/${saveBase64UrlEncode(code)}/history`),
      getLastReceiptLocation: (code: string): Promise<IBinLocation | null> => get(`/api/v1/item/${saveBase64UrlEncode(code)}/history/last_receipt_location`),
      
      stockTransfer: async (
        appName: AppName, 
        data: IStockTransferData[], 
        suppliedKardexData: IStockTransferService$IStockTransferKardexInfo = null
      ): Promise<void> => {
        // if we plainly moved stock to kardex then we need to warn the user
        if (appName !== 'KARDEX STORAGE' && data.some(it => it.to.physicalLocationType === 'KARDEX')) {
          addNotification('info', <Translation name="T.objects.kardex.error.canNotDirectlyMoveToKardexUseKardexStorage"/>)
          throw new Error("prevent stock transfer to kardex")
        }

        const singleItemTargetLocations = data.filter(it => it.to.physicalLocationType === 'SINGLE_ITEM_LOCATION')
        if (singleItemTargetLocations.length > 0) {
          const targetLocations = distinctBy(singleItemTargetLocations.map(it => it.to), it => it.code)
          const stockForSingleItemLocations = await post<IItemBinLocation[]>(`/api/v1/bin_location/multiple/contents`, targetLocations.map(it => it.code))
          for (const targetLocation of targetLocations) {
            const itemsForTargetLocation = distinctBy(stockForSingleItemLocations.filter(it => it.binLocation.code === targetLocation.code).map(it => it.item), it => it.code)
            const itemsMovedToTargetLocation = distinctBy(data.filter(it => it.to.code === targetLocation.code).map(it => it.item), it => it.code)
            const itemsAfterMove = distinctBy([...itemsForTargetLocation, ...itemsMovedToTargetLocation], it => it.code)
            if (itemsForTargetLocation.length <= 1 && itemsAfterMove.length > 1) {
              if (!await confirm(<Translation name="T.warnings.singleItemLocationWillHaveMultipleItemsAfterMove" params={{ targetLocation: <Location inline obj={targetLocation}/>, items: <>{insertBetween(itemsAfterMove.map(it => <Item inline obj={it}/>), ", ")}</> }}/>)) {
                throw new Error("prevent adding new items to single item locations")
              }
            }
          }
        }


        // confirm whether a user really wants to move stock to a receipt location
        if (data.some(it => it.to.locationType === 'RECEIVE')) {
          if (!(await confirm(<Translation name="T.warnings.youAreAboutToMoveToAnReceiveLocation"/>))) {
            throw new Error("prevent movement to receive location")
          }
        }

        // actually process the transfer
        const verified = await post('/api/v1/operations/stock_transfer/validateRequest', data) as IIItemDao$ICompanion$IStockTransferRequest[]
        
        // verify with the specialization
        await window.be_specializations?.augmentations?.warehouse?.stockTransfer?.stockTransferValidation({
          appName, verified
        })

        async function getKardexData() {
          let kardexData = suppliedKardexData
          if (verified.some(it => it.from.physicalLocationType === 'KARDEX') && !kardexData) {
            const message = await prompt(
              <Translation name="T.objects.kardex.stockTransferMessage"/>,
              `Stock transfer to ${distinct(verified.map(it => it.to.visualCode)).join(", ")}`
            )
            if (message === null) {
              throw Error("Cancelled")
            }
            kardexData = {
              documentIdentifier: null,
              documentDescription: null,
              displayText: message
            }
          }
        }

        const lineNeedsQc = verified.map(line => line.allocations.some(alloc => {
          if (line.item.managedBy === 'NORMAL') {
            return false
          }
          if (line.item.managedBy === 'BATCH_NUMBER') {
            return !alloc.batch.qualityControlApprovedOrNotRequired
          }
          if (line.item.managedBy === 'SERIAL_NUMBER') {
            return !alloc.serial.qualityControlApprovedOrNotRequired
          }
          return false
        }))
        const hasQcLines = lineNeedsQc.some(it => it)
        const hasPassedQcLines = lineNeedsQc.some(it => !it)
        if (hasQcLines && hasPassedQcLines) {
          throw addNotification('danger', <Translation name="T.errors.canNotMoveQcAndNonQcInSameTransfer"/>)
        }
        if (hasQcLines) {
          // the target locations should be of type qc/bulk or mobile
          const targetLocations = distinctBy(verified.map(it => it.to), it => it.code)
          targetLocations.forEach(
            it => check(
              ['NORMAL'].includes(it.physicalLocationType), 
              () => <Translation name='T.errors.canOnlyMoveBlockForQcToBulkMobileOrQcTypes'/>
            )
          )

          targetLocations.forEach(
            it => check(
              ['BULK', 'MOBILE', 'QC'].includes(it.locationType), 
              () => <Translation name='T.errors.canOnlyMoveBlockForQcToBulkMobileOrQcTypes'/>
            )
          )

          // the target locations should be empty as well
          await mapAsync(targetLocations, async location => {
            const contents = (await get(`/api/v1/bin_location/${location.code}/contents`)) as any[]
            check(contents.length === 0, () => <Translation name="T.errors.canOnlyMoveQcItemsToEmptyLocations"/>)
          })

          // if we move to type qc/bulk we need to lock them
          const qcBulkLocations = targetLocations.filter(it => ['BULK', 'QC'].includes(it.locationType))
          if (
            qcBulkLocations.length > 0 && 
            !await confirm(
              <Translation 
                name="T.warnings.theFollowingLocationsWillBeBlockedDueToQC" 
                params={{ locations: qcBulkLocations.map(it => it.visualCode).join(", ")}}
              />
            )
          ) {
            return
          }

          // move the stock
          await post(`/api/v1/operations/stock_transfer/${appName}`, {
            instructions: verified,
            kardexData: await getKardexData()
          })

          // lock the qc locations
          await Promise.all(qcBulkLocations.map(async location => {
            const code = location.code
            await post(`/api/v1/bin_location/${code}/setBlockStockMovements/true`, { 
              reason: "Block for QC is required before this location can be unlocked"
            })
          }))
        } else {
          // simple transfer without hassle
          await post(`/api/v1/operations/stock_transfer/${appName}`, {
            instructions: verified,
            kardexData: await getKardexData()
          })
        }
      },

      printLabel: (
        printerId: number,
        jobs: IItemLabelPrintJob,
      ) =>
        post(`/api/v1/item/label/${printerId}`, jobs),

      printPreReceiptLabel: (
        printerId: number,
        job: IPreReceiptPrintLabelJob,
      ) =>
        post(`/api/v1/item/pre_receipt_label/${printerId}`, job),

      printLargeLabel: (
        printerId: number,
        jobs: IItemLabelPrintJob,
      ) =>
        post(`/api/v1/item/large_label/${printerId}`, jobs),

      
      serial: {
        findSerial: (itemCode: string, serialCode: string) => get(`/api/v1/item/serial/find`, { itemCode: saveBase64UrlEncode(itemCode), serialCode: saveBase64UrlEncode(serialCode) }),
        findAllSerialsInStockByCode: (serialCode: string) => get('/api/v1/item/serial/find_all_in_stock', { serialCode: saveBase64UrlEncode(serialCode) }),
        updateSerial: (code: string, payload: Partial<ISerialNumber>) => post(`/api/v1/item/serial/${saveBase64UrlEncode(code)}/update`, payload),
      },
      batch: {
        findBatch: (itemCode: string, batchCode: string) => get(`/api/v1/item/batch/find`, { itemCode: saveBase64UrlEncode(itemCode), batchCode: saveBase64UrlEncode(batchCode) }),
        findAutoComplete: (itemCode: string, batchCode: string) => get(`/api/v1/item/batch/autocomplete`, { itemCode: saveBase64UrlEncode(itemCode), batchCode: saveBase64UrlEncode(batchCode) })
      },
      whole2part: {
        setPart: (itemCode: string, partCode: string, partQuantity: number) =>  post(`/api/v1/item/${saveBase64UrlEncode(itemCode)}/whole2part`, {}, { partItemCode: partCode, partQuantity: partQuantity }),
        process: (data: IWholeToPartDto) => post(`/api/v1/item/whole2part/process`, data),
      }
    },
  };
};
